import { styled } from "@pigment-css/react";
import { SelectMenu } from "@acto/taskpane/field-sales-2/components/primitives/SelectMenu";
import { SearchHeaderRow } from "@acto/taskpane/field-sales-2/components/patterns/Search";
import { type BreakdownMode, CUSTOMERS_OPTION, PRODUCTS_OPTION } from "./useAnalysisBreakdown";

// Floating dock that fades into the top-nav row on scroll, aligned to the right of the close X. The
// page below is untouched — this reuses the same SearchHeaderRow (so the search expand animation is
// identical to the in-content one) with the breakdown dropdown as its title.
const Dock = styled("div")(({ theme }) => ({
  position: "fixed",
  top: theme.fieldSalesTwo.spacing[72],
  left: 0,
  right: 0,
  // Above the sticky ShellTopBar (zIndex 7) so the nav's backdrop gradient doesn't paint over it.
  zIndex: 8,
  // Clear the close X on the left: nav padding + the 50px button + a gap.
  paddingLeft: `calc(${theme.fieldSalesTwo.spacing[16]} + ${theme.fieldSalesTwo.spacing[50]} + ${theme.fieldSalesTwo.spacing[8]})`,
  paddingRight: theme.fieldSalesTwo.spacing[16],
  transition: `padding-left ${theme.fieldSalesTwo.motion.duration.base} ${theme.fieldSalesTwo.motion.easing.emphasized}, opacity ${theme.fieldSalesTwo.motion.duration.base} ${theme.fieldSalesTwo.motion.easing.standard}`,
  // While searching, the expanded search grows over the close X so only its own X shows.
  '&[data-searching="true"]': {
    paddingLeft: theme.fieldSalesTwo.spacing[16],
  },
  // The container never captures pointer events (so it can't block the X or the list in its gaps);
  // only its children do, and only while visible.
  pointerEvents: "none",
  opacity: 0,
  // Fade only — NO transform. A transform here would make the dock the containing block for the
  // breakdown dropdown's position:fixed backdrop, clipping its outside-tap-to-close to the dock row.
  '&[data-visible="true"]': {
    opacity: 1,
  },
  '&[data-visible="true"] > *': {
    pointerEvents: "auto",
  },
}));

interface AnalysisNavDockProps {
  visible: boolean;
  mode: BreakdownMode;
  productsLabel: string;
  customersLabel: string;
  toggleLabel: string;
  searchPlaceholder: string;
  searchValue: string;
  onSearchChange: (value: string) => void;
  /** Switch breakdown; the caller also scrolls back to the top, which fades the dock out. */
  onModeChange: (mode: BreakdownMode) => void;
  /** Controlled search-open state (owned by the caller so the dock stays shown while searching even
   *  if an empty result set collapses the scroll to the top). */
  searchOpen: boolean;
  onSearchOpenChange: (open: boolean) => void;
}

export function AnalysisNavDock({
  visible,
  mode,
  productsLabel,
  customersLabel,
  toggleLabel,
  searchPlaceholder,
  searchValue,
  onSearchChange,
  onModeChange,
  searchOpen,
  onSearchOpenChange,
}: AnalysisNavDockProps) {
  const toggle = (
    <SelectMenu
      variant="pill"
      options={[
        { value: PRODUCTS_OPTION, label: productsLabel },
        { value: CUSTOMERS_OPTION, label: customersLabel },
      ]}
      value={mode === "products" ? PRODUCTS_OPTION : CUSTOMERS_OPTION}
      onSelect={(value) => onModeChange(value === PRODUCTS_OPTION ? "products" : "customers")}
      triggerLabel={toggleLabel}
    />
  );

  return (
    <Dock data-visible={visible} data-searching={searchOpen} aria-hidden={!visible}>
      <SearchHeaderRow
        title={toggle}
        placeholder={searchPlaceholder}
        value={searchValue}
        onChange={onSearchChange}
        expanded={searchOpen}
        onExpandedChange={onSearchOpenChange}
      />
    </Dock>
  );
}
